import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { userApi, type UserAlert } from '../../services/userApi';
import { AlertFormDialog } from './AlertFormDialog';

function criteriaSummary(alert: UserAlert): string {
  const parts: string[] = [];
  if (alert.criteria.caliber?.length) parts.push(alert.criteria.caliber.join(', '));
  if (alert.criteria.brand?.length) parts.push(alert.criteria.brand.join(', '));
  if (alert.criteria.bulletType?.length) parts.push(alert.criteria.bulletType.join('/'));
  if (alert.criteria.bulletWeightGrains?.length) {
    parts.push(`${alert.criteria.bulletWeightGrains.join('/')}gr`);
  }
  if (alert.criteria.maxPricePerRound != null) {
    parts.push(`≤ $${alert.criteria.maxPricePerRound.toFixed(3)}/rd`);
  }
  if (alert.criteria.minRoundCount != null) {
    parts.push(`≥ ${alert.criteria.minRoundCount} rd`);
  }
  if (alert.criteria.excludeReloaded) parts.push('excl. reloaded');
  return parts.length === 0 ? 'Any matching listing' : parts.join(' · ');
}

/**
 * Dashboard alerts page (spec §15.6, §16.3).
 *
 * Lists the user's alerts in a table with name, criteria summary,
 * frequency, channels, active toggle, and last-fired timestamp. Each
 * row has Edit + Delete actions; deletion is confirmed via a dialog
 * with role="dialog" + aria-labelledby (WCAG 2.4.6).
 */
export function AlertsPage() {
  const queryClient = useQueryClient();
  const alertsQuery = useQuery({
    queryKey: ['user', 'alerts'],
    queryFn: () => userApi.listAlerts(),
  });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<UserAlert | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<UserAlert | null>(null);
  // Announced via the role="status" live region below so screen-reader users
  // hear the save confirmation after the dialog closes (spec §16.3).
  const [statusMessage, setStatusMessage] = useState('');

  // Clear the live region a few seconds after each announcement. Without this
  // the message lingers, and a second save with the *same* text (e.g. two
  // "Alert saved." in a row) wouldn't re-announce — assistive tech only fires
  // on content change. Returning the region to empty between announcements
  // guarantees the next identical message is spoken.
  useEffect(() => {
    if (!statusMessage) return undefined;
    const timer = setTimeout(() => setStatusMessage(''), 4000);
    return () => clearTimeout(timer);
  }, [statusMessage]);

  const toggleActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      userApi.updateAlert(id, { isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user', 'alerts'] }),
  });

  const deleteAlert = useMutation({
    mutationFn: (id: string) => userApi.deleteAlert(id),
    onSuccess: () => {
      setConfirmDelete(null);
      return queryClient.invalidateQueries({ queryKey: ['user', 'alerts'] });
    },
  });

  function onNewAlert() {
    setEditing(null);
    setFormOpen(true);
  }

  function onEditAlert(alert: UserAlert) {
    setEditing(alert);
    setFormOpen(true);
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Typography variant="h1" component="h1" sx={{ fontSize: '1.6rem' }}>
          Alerts
        </Typography>
        <Button variant="contained" onClick={onNewAlert}>
          New alert
        </Button>
      </Stack>

      {alertsQuery.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Loading alerts" />
        </Box>
      ) : alertsQuery.isError ? (
        <Alert severity="error" role="alert">
          Failed to load your alerts: {(alertsQuery.error as Error).message}
        </Alert>
      ) : (alertsQuery.data?.alerts.length ?? 0) === 0 ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="body1">You haven’t created any alerts yet.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Create one to be notified when listings match your criteria.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table aria-label="Your alerts">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Criteria</TableCell>
                <TableCell>Frequency</TableCell>
                <TableCell>Channels</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Last fired</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {alertsQuery.data!.alerts.map((alert) => (
                <TableRow key={alert.id} hover>
                  <TableCell>{alert.name}</TableCell>
                  <TableCell>{criteriaSummary(alert)}</TableCell>
                  <TableCell>{alert.frequency}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {alert.channels.map((c) => (
                        <Chip key={c} label={c} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={alert.isActive}
                      onChange={(e) =>
                        toggleActive.mutate({ id: alert.id, isActive: e.target.checked })
                      }
                      inputProps={{ 'aria-label': `Toggle alert ${alert.name} active` }}
                    />
                  </TableCell>
                  <TableCell>{alert.lastFiredAt ?? '—'}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" onClick={() => onEditAlert(alert)}>
                        Edit
                      </Button>
                      <IconButton
                        aria-label={`Delete alert ${alert.name}`}
                        onClick={() => setConfirmDelete(alert)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Visually-hidden live region: announces save confirmations (§16.3).
          role="status" implies aria-live="polite" + aria-atomic. */}
      <Box
        role="status"
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          margin: -1,
          padding: 0,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {statusMessage}
      </Box>

      <AlertFormDialog
        open={formOpen}
        alert={editing}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setStatusMessage(editing ? 'Alert updated.' : 'Alert saved.');
          return queryClient.invalidateQueries({ queryKey: ['user', 'alerts'] });
        }}
      />

      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        aria-labelledby="confirm-delete-alert-title"
      >
        <DialogTitle id="confirm-delete-alert-title">Delete this alert?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            “{confirmDelete?.name}” will be permanently removed. This can’t be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => confirmDelete && deleteAlert.mutate(confirmDelete.id)}
            disabled={deleteAlert.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Small inline SVG icon so we don't pull in another icon import.
function DeleteIcon() {
  return (
    <Box
      component="svg"
      width={20}
      height={20}
      viewBox="0 0 24 24"
      aria-hidden="true"
      sx={{ fill: 'currentColor' }}
    >
      <path d="M6 7v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6zm3-3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1h5v2H4V5h5V4z" />
    </Box>
  );
}

export default AlertsPage;
